import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import api from "@/lib/api";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import SEO from "@/components/SEO";

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [orderId, setOrderId] = useState("");
  const { clearCart } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    clearCart();
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) {
        setStatus("timeout");
        return;
      }
      attempts++;
      try {
        const { data } = await api.get(`/orders/checkout/status/${sessionId}`);
        setOrderId(data.order_id || "");
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page" className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <SEO title="Order Confirmed | AERWEAR" description="AERWEAR account and order tools." noindex />
      <div className="max-w-md w-full text-center">
        {status === "loading" && (
          <>
            <Loader2 size={48} className="animate-spin text-gray-400 mx-auto mb-6" />
            <h1 className="text-2xl font-black uppercase tracking-tighter font-['Cabinet_Grotesk'] mb-2">Processing Payment</h1>
            <p className="text-sm text-gray-500 font-['Outfit']">Please wait while we confirm your order...</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle size={48} className="text-green-600 mx-auto mb-6" />
            <h1 className="text-3xl font-black uppercase tracking-tighter font-['Cabinet_Grotesk'] mb-2">Order Confirmed</h1>
            <p className="text-sm text-gray-500 font-['Outfit'] mb-2">Thank you for your purchase.</p>
            {orderId && <p className="text-xs text-gray-400 font-['Outfit'] mb-8">Order #{orderId.slice(0, 8)}</p>}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/account/orders" data-testid="view-orders-btn" className="border border-[#0A0A0A] px-6 py-3 text-xs uppercase tracking-[0.15em] font-bold hover:bg-[#0A0A0A] hover:text-white transition-colors">
                View Orders
              </Link>
              <Link to="/" data-testid="continue-shopping-btn" className="bg-[#0A0A0A] text-white px-6 py-3 text-xs uppercase tracking-[0.15em] font-bold hover:bg-[#1a1a1a] transition-colors">
                Continue Shopping
              </Link>
            </div>
          </>
        )}

        {(status === "error" || status === "expired" || status === "timeout") && (
          <>
            <XCircle size={48} className="text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-black uppercase tracking-tighter font-['Cabinet_Grotesk'] mb-2">
              {status === "timeout" ? "Payment Pending" : "Payment Issue"}
            </h1>
            <p className="text-sm text-gray-500 font-['Outfit'] mb-8">
              {status === "timeout"
                ? "Your payment is still being processed. Check your email for confirmation."
                : "Something went wrong. Please try again or contact support."}
            </p>
            <Link to="/" data-testid="back-home-btn" className="bg-[#0A0A0A] text-white px-6 py-3 text-xs uppercase tracking-[0.15em] font-bold hover:bg-[#1a1a1a] transition-colors">
              Back to Home
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
